import { BiSolidBank } from "react-icons/bi";
import { useMemo } from "react";
import { City, Country, State } from "country-state-city";
import Dropdown from "../form/dropDown";
import Input from "../form/Input";

function RecipientInput({ label, className = "", inputClassName = "", ...props }) {
    return (
        <Input
            {...props}
            required={false}
            label={label}
            labelClassName="!mt-0 text-[#101828]"
            className={`!mt-0 ${className}`}
            inputClassName={inputClassName}
        />
    );
}

export function UsdRoutingNumberField({
    error,
    loading,
    routingNumber,
    setRoutingNumber,
}) {
    return (
        <div className="relative">
            <RecipientInput
                error={error}
                inputClassName={loading ? "!pr-12" : ""}
                inputMode="numeric"
                label="Routing number"
                onChange={(value) => setRoutingNumber(value.replace(/\D/g, "").slice(0, 9))}
                placeholder="Enter recipient's routing number"
                value={routingNumber}
            />
            {loading && (
                <span
                    aria-label="Verifying routing number"
                    className="absolute bottom-[18px] right-4 h-5 w-5 animate-spin rounded-full border-2 border-[#D8CCFF] border-t-[#6F00FF]"
                    role="status"
                />
            )}
        </div>
    );
}

const US_STATE_OPTIONS = [
    ["AL", "Alabama"],
    ["AK", "Alaska"],
    ["AZ", "Arizona"],
    ["AR", "Arkansas"],
    ["CA", "California"],
    ["CO", "Colorado"],
    ["CT", "Connecticut"],
    ["DE", "Delaware"],
    ["FL", "Florida"],
    ["GA", "Georgia"],
    ["HI", "Hawaii"],
    ["ID", "Idaho"],
    ["IL", "Illinois"],
    ["IN", "Indiana"],
    ["IA", "Iowa"],
    ["KS", "Kansas"],
    ["KY", "Kentucky"],
    ["LA", "Louisiana"],
    ["ME", "Maine"],
    ["MD", "Maryland"],
    ["MA", "Massachusetts"],
    ["MI", "Michigan"],
    ["MN", "Minnesota"],
    ["MS", "Mississippi"],
    ["MO", "Missouri"],
    ["MT", "Montana"],
    ["NE", "Nebraska"],
    ["NV", "Nevada"],
    ["NH", "New Hampshire"],
    ["NJ", "New Jersey"],
    ["NM", "New Mexico"],
    ["NY", "New York"],
    ["NC", "North Carolina"],
    ["ND", "North Dakota"],
    ["OH", "Ohio"],
    ["OK", "Oklahoma"],
    ["OR", "Oregon"],
    ["PA", "Pennsylvania"],
    ["RI", "Rhode Island"],
    ["SC", "South Carolina"],
    ["SD", "South Dakota"],
    ["TN", "Tennessee"],
    ["TX", "Texas"],
    ["UT", "Utah"],
    ["VT", "Vermont"],
    ["VA", "Virginia"],
    ["WA", "Washington"],
    ["WV", "West Virginia"],
    ["WI", "Wisconsin"],
    ["WY", "Wyoming"],
    ["DC", "District of Columbia"],
].map(([value, label]) => ({ value, label }));

function BankPickerCard({ selectedBank, onClick }) {
    return (
        <div
            className="flex items-center justify-between p-3 bg-[#FFF] rounded-lg cursor-pointer"
            onClick={onClick}
        >
            <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#F2F3FC] rounded-full flex items-center justify-center overflow-hidden">
                    <BiSolidBank size={22} />
                </div>
                <span className="text-[#344054] font-medium">
                    {selectedBank?.name || "Choose bank"}
                </span>
            </div>
            <span className="text-[#667085] font-thin text-3xl">›</span>
        </div>
    );
}

function CryptoRecipientSection({ networkDisplayName, cryptoAddress, currency, onAddressChange }) {
    return (
        <>
            {/* Selected network */}
            <div className="rounded-2xl bg-[#F2F3FC] px-5 py-4 border border-[#E7E9F4]">
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-[#667085] text-sm md:text-base font-normal">
                            Selected network
                        </p>
                        <p className="mt-2 text-[20px] md:text-[26px] font-normal text-[#161827]">
                            {networkDisplayName || "—"}
                        </p>
                    </div>
                </div>
            </div>

            {/* Address */}
            <div className="mt-6">
                <RecipientInput
                    label="Address"
                    value={cryptoAddress}
                    onChange={(value) => onAddressChange({ target: { value } })}
                    placeholder={`Enter valid ${currency} wallet address`}
                />
            </div>
        </>
    );
}

function AccountHolderSection({
    holderType,
    setHolderType,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    businessName,
    setBusinessName,
}) {
    return (
        <>
            <div>
                <Dropdown
                    label="Account holder type"
                    labelClassName="!mt-0 text-[#101828]"
                    value={holderType}
                    options={[
                        { value: "individual", label: "Individual" },
                        { value: "corporate", label: "Corporate" },
                    ]}
                    onChange={setHolderType}
                    placeholder="Select an option"
                    className="!mt-0"
                />
            </div>

            {holderType === "individual" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <RecipientInput
                            label="First name"
                            placeholder="First name of account holder"
                            value={firstName}
                            onChange={setFirstName}
                        />
                    </div>
                    <div>
                        <RecipientInput
                            label="Last name"
                            placeholder="Last name of account holder"
                            value={lastName}
                            onChange={setLastName}
                        />
                    </div>
                </div>
            )}

            {holderType === "corporate" && (
                <div>
                    <RecipientInput
                        label="Corporation name"
                        placeholder="Name of corporation"
                        value={businessName}
                        onChange={setBusinessName}
                    />
                </div>
            )}
        </>
    );
}

function RecipientAddressSection({
    recStreet,
    setRecStreet,
    recCity,
    setRecCity,
    recState,
    setRecState,
    recZip,
    setRecZip,
}) {
    return (
        <div>
            <RecipientInput
                type="textarea"
                label="Recipient’s address"
                placeholder="Street address"
                value={recStreet}
                onChange={setRecStreet}
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                <RecipientInput placeholder="City" value={recCity} onChange={setRecCity} />
                <RecipientInput placeholder="State" value={recState} onChange={setRecState} />
                <RecipientInput
                    placeholder="ZIP / Postal code"
                    value={recZip}
                    onChange={setRecZip}
                />
            </div>
        </div>
    );
}

function UsdIbanSection(props) {
    const {
        bankCountryCode,
        setBankCountryCode,
        bankName,
        setBankName,
        accountName,
        setAccountName,
        holderType,
        setHolderType,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        businessName,
        setBusinessName,
        recStreet,
        setRecStreet,
        recCountry,
        setRecCountry,
        recCity,
        setRecCity,
        recState,
        setRecState,
        recZip,
        setRecZip,
        iban,
        setIban,
        bic,
        setBic,
        relationship,
        setRelationship,
        recipientEmail,
        setRecipientEmail,
    } = props;

    const countryOptions = useMemo(
        () =>
            Country.getAllCountries()
                .filter((country) => country.isoCode !== "US")
                .map((country) => {
                    const code = country.isoCode;
                    const name = country.name;
                    return {
                        value: code,
                        displayValue: name,
                        searchLabel: `${name} ${code}`,
                        label: (
                            <span className="flex items-center gap-2">
                                <img
                                    src={`https://flagcdn.com/w40/${code.toLowerCase()}.png`}
                                    alt=""
                                    className="h-5 w-5 rounded-full object-cover"
                                />
                                <span>{name}</span>
                            </span>
                        ),
                    };
                }),
        []
    );
    const stateOptions = useMemo(
        () =>
            recCountry
                ? State.getStatesOfCountry(recCountry).map((state) => ({
                      value: state.isoCode,
                      label: state.name,
                  }))
                : [],
        [recCountry]
    );
    const cityOptions = useMemo(
        () =>
            recCountry && recState
                ? City.getCitiesOfState(recCountry, recState).map((city) => ({
                      value: city.name,
                      label: city.name,
                  }))
                : [],
        [recCountry, recState]
    );

    return (
        <>
            <div>
                <RecipientInput
                    label="Account number or IBAN"
                    placeholder="Enter an account number or IBAN"
                    value={iban}
                    onChange={(value) => setIban(value.toUpperCase().replace(/\s+/g, ""))}
                />
            </div>

            <div>
                <RecipientInput
                    label="SWIFT or BIC"
                    placeholder="Enter a SWIFT or BIC"
                    value={bic}
                    onChange={(value) => setBic(value.toUpperCase().replace(/\s+/g, ""))}
                />
            </div>

            <div>
                <RecipientInput
                    label="Account name"
                    placeholder="Enter name on account"
                    value={accountName}
                    onChange={setAccountName}
                />
                <p className="mt-1 text-xs text-[#667085]">
                    This recipient will be automatically saved as a beneficiary for future transfers
                </p>
            </div>

            <div>
                <Dropdown
                    label="Recipient bank country"
                    labelClassName="!mt-0 text-[#101828]"
                    value={bankCountryCode}
                    options={countryOptions}
                    onChange={(value) => {
                        setBankCountryCode(value);
                    }}
                    placeholder="Select bank country"
                    searchable
                    className="!mt-0"
                />
            </div>

            <div>
                <RecipientInput
                    label="Recipient bank name"
                    placeholder="Enter bank name"
                    value={bankName}
                    onChange={setBankName}
                />
            </div>

            <div>
                <Dropdown
                    label="Recipient type"
                    labelClassName="!mt-0 text-[#101828]"
                    value={holderType}
                    options={[
                        { value: "individual", label: "Individual" },
                        { value: "corporate", label: "Business" },
                    ]}
                    onChange={setHolderType}
                    placeholder="Select an option"
                    className="!mt-0"
                />
            </div>

            {holderType === "individual" ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                        <RecipientInput
                            label="First name of recipient"
                            placeholder="Enter first name"
                            value={firstName}
                            onChange={setFirstName}
                        />
                    </div>
                    <div>
                        <RecipientInput
                            label="Last name of recipient"
                            placeholder="Enter last name"
                            value={lastName}
                            onChange={setLastName}
                        />
                    </div>
                </div>
            ) : holderType === "corporate" ? (
                <RecipientInput
                    label="Business name"
                    placeholder="Enter business name"
                    value={businessName}
                    onChange={setBusinessName}
                />
            ) : null}

            <div>
                <Dropdown
                    label="Relationship"
                    labelClassName="!mt-0 text-[#101828]"
                    value={relationship}
                    options={[
                        "Vendor",
                        "Customer",
                        "Partner",
                        "Supplier",
                        "Employee",
                        "Contractor",
                        "Other",
                    ].map((label) => ({ value: label.toLowerCase(), label }))}
                    onChange={setRelationship}
                    placeholder="Select relationship with recipient"
                    className="!mt-0"
                />
            </div>

            <RecipientInput
                type="email"
                label="Recipient’s email"
                placeholder="Enter recipient's email address"
                value={recipientEmail}
                onChange={setRecipientEmail}
            />

            <div>
                <RecipientInput
                    type="textarea"
                    label="Recipient’s address"
                    placeholder="Street address"
                    value={recStreet}
                    onChange={setRecStreet}
                />
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <Dropdown
                        value={recCountry}
                        options={countryOptions}
                        onChange={(value) => {
                            setRecCountry(value);
                            setRecState("");
                            setRecCity("");
                        }}
                        placeholder="Country"
                        searchable
                        className="!mt-0"
                    />
                    {stateOptions.length ? (
                        <Dropdown
                            value={recState}
                            options={stateOptions}
                            onChange={(value) => {
                                setRecState(value);
                                setRecCity("");
                            }}
                            placeholder="State"
                            searchable
                            className="!mt-0"
                        />
                    ) : (
                        <RecipientInput
                            placeholder="State"
                            value={recState}
                            onChange={setRecState}
                        />
                    )}
                    {cityOptions.length ? (
                        <Dropdown
                            value={recCity}
                            options={cityOptions}
                            onChange={setRecCity}
                            placeholder="City"
                            searchable
                            className="!mt-0"
                        />
                    ) : (
                        <RecipientInput placeholder="City" value={recCity} onChange={setRecCity} />
                    )}
                    <RecipientInput
                        placeholder="ZIP / Postal Code"
                        value={recZip}
                        onChange={setRecZip}
                    />
                </div>
            </div>
        </>
    );
}

function UsdSwiftSection(props) {
    const {
        COUNTRY_OPTIONS,
        bankCountryCode,
        setBankCountryCode,
        bankName,
        setBankName,
        holderType,
        setHolderType,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        businessName,
        setBusinessName,
        recStreet,
        setRecStreet,
        recCity,
        setRecCity,
        recState,
        setRecState,
        recZip,
        setRecZip,
        accountNumber,
        setAccountNumber,
        bic,
        setBic,
        verifiedBankDetails,
    } = props;

    return (
        <>
            <div>
                <RecipientInput
                    disabled={Boolean(verifiedBankDetails?.bankName)}
                    label="Bank name"
                    onChange={setBankName}
                    placeholder="Enter bank name"
                    value={bankName}
                />
            </div>

            {/* Country of bank */}
            <div>
                <Dropdown
                    label="Country of bank"
                    labelClassName="!mt-0 text-[#101828]"
                    value={bankCountryCode}
                    options={COUNTRY_OPTIONS}
                    onChange={setBankCountryCode}
                    placeholder="Select a country"
                    readOnly={Boolean(verifiedBankDetails?.bankCountry)}
                    className="!mt-0"
                />
            </div>

            {/* Account holder */}
            <AccountHolderSection
                holderType={holderType}
                setHolderType={setHolderType}
                firstName={firstName}
                setFirstName={setFirstName}
                lastName={lastName}
                setLastName={setLastName}
                businessName={businessName}
                setBusinessName={setBusinessName}
            />

            {/* Account number */}
            <div>
                <RecipientInput
                    label="Account number"
                    inputMode="numeric"
                    placeholder="Enter recipient's account number"
                    value={accountNumber}
                    onChange={(value) => setAccountNumber(value.replace(/\D/g, ""))}
                />
            </div>

            {/* BIC */}
            <div>
                <RecipientInput
                    disabled={Boolean(verifiedBankDetails?.bic)}
                    label="BIC"
                    placeholder="e.g., AAAA-BB-CC-123"
                    inputClassName="uppercase"
                    value={bic}
                    onChange={(value) => setBic(value.toUpperCase().trim())}
                />
            </div>

            {/* Address */}
            <RecipientAddressSection
                recStreet={recStreet}
                setRecStreet={setRecStreet}
                recCity={recCity}
                setRecCity={setRecCity}
                recState={recState}
                setRecState={setRecState}
                recZip={recZip}
                setRecZip={setRecZip}
            />
        </>
    );
}

function UsdAchSection(props) {
    const {
        holderType,
        setHolderType,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        businessName,
        setBusinessName,
        accountType,
        setAccountType,
        ACCOUNT_TYPE_OPTIONS,
        accountNumber,
        setAccountNumber,
        accountName,
        setAccountName,
        bankName,
        setBankName,
        bankStreet,
        setBankStreet,
        bankCountry,
        setBankCountry,
        bankState,
        setBankState,
        bankCity,
        setBankCity,
        bankZip,
        setBankZip,
        relationship,
        setRelationship,
        recipientEmail,
        setRecipientEmail,
        recStreet,
        setRecStreet,
        recCountry,
        setRecCountry,
        recCity,
        setRecCity,
        recState,
        setRecState,
        recZip,
        setRecZip,
        verifiedBankDetails,
    } = props;

    const countryOptions = [{ value: "US", label: "United States" }];
    const addressFields = ({
        street,
        setStreet,
        country,
        setCountry,
        state,
        setState,
        city,
        setCity,
        zip,
        setZip,
        readOnlyFields = {},
    }) => {
        const stateOptions = country === "US" ? US_STATE_OPTIONS : [];

        return (
            <>
                <RecipientInput
                    disabled={Boolean(readOnlyFields.street)}
                    type="textarea"
                    placeholder="Street address"
                    value={street}
                    onChange={setStreet}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    <Dropdown
                        readOnly={Boolean(readOnlyFields.country)}
                        value={country}
                        options={countryOptions}
                        onChange={(value) => {
                            setCountry(value);
                            setState("");
                            setCity("");
                        }}
                        placeholder="Country"
                        searchable
                        className="!mt-0"
                    />
                    <Dropdown
                        readOnly={Boolean(readOnlyFields.state)}
                        value={state}
                        options={stateOptions}
                        onChange={(value) => {
                            setState(value);
                            setCity("");
                        }}
                        placeholder="State"
                        searchable
                        className="!mt-0"
                    />
                    <RecipientInput disabled={Boolean(readOnlyFields.city)} placeholder="City" value={city} onChange={setCity} />
                    <RecipientInput disabled={Boolean(readOnlyFields.zip)} placeholder="ZIP / Postal Code" value={zip} onChange={setZip} />
                </div>
            </>
        );
    };

    return (
        <>
            <div>
                <RecipientInput
                    label="Account number"
                    inputMode="numeric"
                    placeholder="Enter an account number"
                    value={accountNumber}
                    onChange={(value) => setAccountNumber(value.replace(/\D/g, ""))}
                />
            </div>

            <div>
                <RecipientInput
                    disabled={Boolean(verifiedBankDetails?.accountName)}
                    label="Account name"
                    placeholder="Enter name on account"
                    value={accountName}
                    onChange={setAccountName}
                />
                <p className="mt-1 text-xs text-[#667085]">
                    This recipient will be automatically saved as a beneficiary for future transfers
                </p>
            </div>

            <div>
                <RecipientInput
                    disabled={Boolean(verifiedBankDetails?.bankName)}
                    label="Recipient bank name"
                    placeholder="Enter bank name"
                    value={bankName}
                    onChange={setBankName}
                />
            </div>

            <div>
                <Dropdown
                    label="Account type"
                    labelClassName="!mt-0 text-[#101828]"
                    value={accountType}
                    options={ACCOUNT_TYPE_OPTIONS}
                    onChange={setAccountType}
                    placeholder="Select an option"
                    className="!mt-0"
                />
            </div>

            <div>
                <p className="mb-2 text-sm font-medium text-[#101828]">Bank address</p>
                {addressFields({
                    street: bankStreet,
                    setStreet: setBankStreet,
                    country: bankCountry,
                    setCountry: setBankCountry,
                    state: bankState,
                    setState: setBankState,
                    city: bankCity,
                    setCity: setBankCity,
                    zip: bankZip,
                    setZip: setBankZip,
                    readOnlyFields: {
                        street: verifiedBankDetails?.bankStreet,
                        country: verifiedBankDetails?.bankCountry,
                        state: verifiedBankDetails?.bankState,
                        city: verifiedBankDetails?.bankCity,
                        zip: verifiedBankDetails?.bankZip,
                    },
                })}
            </div>

            <div>
                <Dropdown
                    label="Recipient type"
                    labelClassName="!mt-0 text-[#101828]"
                    value={holderType}
                    options={[
                        { value: "individual", label: "Individual" },
                        { value: "corporate", label: "Business" },
                    ]}
                    onChange={setHolderType}
                    placeholder="Select an option"
                    className="!mt-0"
                />
            </div>

            {holderType === "individual" ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <RecipientInput
                            label="First name of recipient"
                            placeholder="Enter first name"
                            value={firstName}
                            onChange={setFirstName}
                        />
                    </div>
                    <div>
                        <RecipientInput
                            label="Last name of recipient"
                            placeholder="Enter last name"
                            value={lastName}
                            onChange={setLastName}
                        />
                    </div>
                </div>
            ) : holderType === "corporate" ? (
                <div>
                    <RecipientInput
                        label="Business name"
                        placeholder="Enter business name"
                        value={businessName}
                        onChange={setBusinessName}
                    />
                </div>
            ) : null}

            <div>
                <Dropdown
                    label="Relationship"
                    labelClassName="!mt-0 text-[#101828]"
                    value={relationship}
                    options={[
                        "Vendor",
                        "Customer",
                        "Partner",
                        "Supplier",
                        "Employee",
                        "Contractor",
                        "Other",
                    ].map((label) => ({ value: label.toLowerCase(), label }))}
                    onChange={setRelationship}
                    placeholder="Select relationship with recipient"
                    className="!mt-0"
                />
            </div>

            <div>
                <RecipientInput
                    type="email"
                    label="Recipient’s email"
                    placeholder="Enter recipient's email address"
                    value={recipientEmail}
                    onChange={setRecipientEmail}
                />
            </div>

            <div>
                <p className="mb-2 text-sm font-medium text-[#101828]">Recipient’s address</p>
                {addressFields({
                    street: recStreet,
                    setStreet: setRecStreet,
                    country: recCountry,
                    setCountry: setRecCountry,
                    state: recState,
                    setState: setRecState,
                    city: recCity,
                    setCity: setRecCity,
                    zip: recZip,
                    setZip: setRecZip,
                })}
            </div>
        </>
    );
}

function NonUsdRecipientSection({
    isKES,
    accountNumber,
    setAccountNumber,
    accountName,
    setAccountName,
    loading,
    MIN_DIGITS,
    MAX_DIGITS,
}) {
    return (
        <>
            <RecipientInput
                label="Account number"
                inputMode="numeric"
                maxLength={MAX_DIGITS}
                placeholder="Enter account number"
                value={accountNumber}
                onChange={(value) => {
                    const digits = value.replace(/\D/g, "").slice(0, MAX_DIGITS);
                    setAccountNumber(digits);
                    if (digits.length < MIN_DIGITS) {
                        setAccountName("");
                    }
                }}
            />

            {isKES ? (
                <div className="mt-3">
                    <RecipientInput
                        label="Account name"
                        placeholder="Enter account name"
                        value={accountName}
                        onChange={setAccountName}
                    />
                </div>
            ) : (
                <div className="mt-3 flex h-[56px] w-full items-center rounded-lg bg-[#f1f5f9] px-4 text-sm text-[#667085]">
                    {loading ? "Validating..." : accountName || "No account selected"}
                </div>
            )}
        </>
    );
}
export {
    BankPickerCard,
    CryptoRecipientSection,
    AccountHolderSection,
    RecipientAddressSection,
    UsdIbanSection,
    UsdSwiftSection,
    UsdAchSection,
    NonUsdRecipientSection,
};
